import { Check, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import {
  SUBMIT_STAGES,
  STALL_NOTICE,
  COMPLETION_TITLE,
  COMPLETION_MESSAGE,
  ELAPSED_LABEL,
  formatElapsed,
  stageDef,
  stageStateFor,
  type SubmitStageId,
} from "@/lib/submit-progress";

interface Props {
  activeStage: SubmitStageId | null;
  finished: boolean;
  elapsedMs: number;
  stalled: boolean;
  error: string | null;
  onRetry: () => void;
  onSaveForLater: () => void;
}

/**
 * Compact, honest submission progress panel.
 *
 * Stage status comes from real completed operations, elapsed time is the only
 * number shown (never a percentage or an ETA), and every status is conveyed by
 * icon + text, not colour alone. The active stage carries the emphasis: a
 * spinner, darker/medium text and a single line of helper copy. Completed and
 * upcoming stages stay compact and quiet.
 */
export function SubmissionProgress({
  activeStage,
  finished,
  elapsedMs,
  stalled,
  error,
  onRetry,
  onSaveForLater,
}: Props) {
  const status = error
    ? null
    : finished
      ? COMPLETION_MESSAGE
      : activeStage
        ? stageDef(activeStage).statusMessage
        : null;

  return (
    <div
      data-testid="submission-progress"
      className="mt-4 w-full max-w-full overflow-hidden rounded-xl border border-border bg-card p-4"
    >
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
        {finished && !error && (
          <CheckCircle2 className="h-4 w-4 shrink-0 text-savings" aria-hidden="true" />
        )}
        <span>
          {error
            ? "Submission Paused"
            : finished
              ? COMPLETION_TITLE
              : "Preparing Your Review"}
        </span>
      </div>

      {/* Live status line — always mirrors the real current stage. */}
      <p
        className="mt-1 text-xs font-medium text-foreground/90"
        aria-live="polite"
        data-testid="submission-status-message"
      >
        {error
          ? "Nothing was lost — your uploaded files and details are still here."
          : status}
      </p>

      {!error && !finished && (
        <p className="mt-1 text-xs text-muted-foreground">
          Please keep this page open while we securely save your invoice and prepare checkout.
        </p>
      )}

      <ol className="mt-3 space-y-1.5" aria-live="polite" aria-atomic="false">
        {SUBMIT_STAGES.map((s) => {
          const state = stageStateFor(s.id, activeStage, finished);
          const isActive = state === "active";
          return (
            <li
              key={s.id}
              data-stage={s.id}
              data-state={state}
              className={
                state === "upcoming"
                  ? "text-xs text-muted-foreground"
                  : isActive
                    ? "text-xs font-medium text-foreground"
                    : "text-xs text-muted-foreground"
              }
            >
              <div className="flex items-center gap-2">
                <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                  {state === "complete" ? (
                    <Check className="h-3.5 w-3.5 text-savings" aria-hidden="true" />
                  ) : isActive ? (
                    <Loader2
                      className="h-3.5 w-3.5 animate-spin text-trust motion-reduce:animate-none"
                      aria-hidden="true"
                    />
                  ) : (
                    <span
                      className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40"
                      aria-hidden="true"
                    />
                  )}
                </span>
                <span className="min-w-0 break-words">
                  {state === "complete" ? s.doneLabel : isActive ? `${s.label}…` : s.label}
                </span>
                <span className="sr-only">
                  {state === "complete"
                    ? " — completed"
                    : isActive
                      ? " — in progress"
                      : " — waiting"}
                </span>
              </div>
              {/* Helper copy belongs to the active stage only. */}
              {isActive && (
                <p
                  className="ml-6 mt-0.5 text-xs font-normal text-muted-foreground"
                  data-testid="submission-helper-text"
                >
                  {s.helperText}
                </p>
              )}
            </li>
          );
        })}
      </ol>

      <div className="mt-3 text-xs text-muted-foreground" aria-live="polite">
        {ELAPSED_LABEL}: {formatElapsed(elapsedMs)}
      </div>


      {stalled && !error && (
        <p className="mt-2 flex items-start gap-2 text-xs text-foreground" role="status">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>{STALL_NOTICE}</span>
        </p>
      )}

      {error && (
        <div className="mt-3">
          <p className="flex items-start gap-2 text-xs text-destructive" role="alert">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex min-h-11 items-center justify-center rounded-lg bg-savings px-4 text-xs font-semibold text-savings-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              Try Again
            </button>
            <button
              type="button"
              onClick={onSaveForLater}
              className="inline-flex min-h-11 items-center justify-center rounded-lg border border-border px-4 text-xs font-semibold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              Save &amp; Return Later
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
